import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Modal, SectionHeader, Spinner, StatCard } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const LEVEL_COLORS = { A1:'border-t-gold-400', A2:'border-t-brand-500', B1:'border-t-green-500', B2:'border-t-de-black' };
const LEVEL_DESC   = { A1:'Beginner', A2:'Elementary', B1:'Intermediate', B2:'Upper-Intermediate' };

export default function PricingPage() {
  const { addToast } = useToast();
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null); // { level, price, duration_weeks }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/super-admin/pricing')
      .then(({ data }) => setPricing(data.pricing || []))
      .catch(() => addToast('Failed to load pricing', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      await API.put(`/super-admin/pricing/${editModal.level}`, {
        price: parseFloat(editModal.price),
        duration_weeks: parseInt(editModal.duration_weeks),
      });
      addToast(`Level ${editModal.level} pricing updated!`, 'success');
      setPricing(prev => prev.map(p => p.level === editModal.level
        ? { ...p, price: parseFloat(editModal.price), duration_weeks: parseInt(editModal.duration_weeks) }
        : p
      ));
      setEditModal(null);
    } catch (err) {
      addToast(err.message || 'Failed to update', 'error');
    } finally {
      setSaving(false);
    }
  };

  const totalRevenuePotential = pricing.reduce((sum, p) => sum + parseFloat(p.price || 0), 0);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <SectionHeader
        title="Course Pricing"
        subtitle="Set prices and durations for each CEFR level. Changes reflect immediately on the registration page."
      />

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" className="text-brand-500" /></div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {pricing.map(p => (
              <StatCard
                key={p.level}
                label={`Level ${p.level}`}
                value={`GHS ${parseFloat(p.price || 0).toFixed(2)}`}
                icon={p.level === 'A1' ? '🌱' : p.level === 'A2' ? '📗' : p.level === 'B1' ? '📘' : '📙'}
                color={p.level === 'A1' ? 'gold' : p.level === 'A2' ? 'red' : 'green'}
              />
            ))}
          </div>

          {/* Pricing cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {pricing.map(p => (
              <Card key={p.level} className={`p-6 border-t-4 ${LEVEL_COLORS[p.level] || ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl font-bold text-de-black">{p.level}</span>
                      <span className="text-sm text-gray-500 font-medium">{LEVEL_DESC[p.level]}</span>
                    </div>
                    <p className="text-3xl font-bold text-brand-500">
                      GHS {parseFloat(p.price || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{p.currency || 'GHS'}</p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditModal({ level: p.level, price: p.price, duration_weeks: p.duration_weeks || 8 })}
                  >
                    Edit
                  </Button>
                </div>

                <div className="space-y-2 pt-3 border-t border-gray-100">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Duration</span>
                    <span className="font-semibold text-de-black">{p.duration_weeks || 8} weeks</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Last updated</span>
                    <span className="text-gray-500 text-xs">
                      {p.updated_at ? new Date(p.updated_at).toLocaleDateString('en-GB') : 'Never'}
                    </span>
                  </div>
                  {p.updated_by_name && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Updated by</span>
                      <span className="text-xs text-gray-500">{p.updated_by_name}</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-6 p-4 bg-surface rounded-xl border border-gray-200">
            <p className="text-sm text-gray-600">
              💡 <strong>Note:</strong> Price changes take effect immediately on the student registration page.
              Students currently enrolled are not affected — only new registrations.
            </p>
          </div>
        </>
      )}

      {/* Edit Modal */}
      <Modal isOpen={!!editModal} onClose={() => setEditModal(null)} title={`Edit Level ${editModal?.level} Pricing`} size="sm">
        {editModal && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-de-black mb-1.5">Price (GHS)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500">GHS</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editModal.price}
                  onChange={e => setEditModal(p => ({ ...p, price: e.target.value }))}
                  className="w-full pl-14 pr-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-de-black mb-1.5">Duration (weeks)</label>
              <input
                type="number"
                min="1"
                max="52"
                value={editModal.duration_weeks}
                onChange={e => setEditModal(p => ({ ...p, duration_weeks: e.target.value }))}
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
              />
            </div>

            <div className="p-3 bg-gold-50 border border-gold-400 rounded-xl">
              <p className="text-xs font-semibold text-de-black">Preview</p>
              <p className="text-sm text-gray-700 mt-1">
                Level <strong>{editModal.level}</strong> will cost{' '}
                <strong className="text-brand-500">GHS {parseFloat(editModal.price || 0).toFixed(2)}</strong>{' '}
                for a <strong>{editModal.duration_weeks}-week</strong> course.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setEditModal(null)}>Cancel</Button>
              <Button variant="primary" loading={saving} onClick={handleSave}>Save Changes</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
